import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import TestimonialsManagement from '../WidgetEditComponents/TestimonialsManagement';
import ImageTooBigPopup from '../WidgetEditComponents/ImageTooBigPopup';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function SimplisticTestimonialEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.testimonialLayout);
    
    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 SimplisticTestimonial layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'testimonialLayout', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // Upload States für dynamische Testimonials
    const [uploadingStates, setUploadingStates] = useState({});
    const [showImageTooBigPopup, setShowImageTooBigPopup] = useState(false);
    const [tooBigFileInfo, setTooBigFileInfo] = useState({ size: 0, name: '' });
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);
    
    const setUploadingState = (index, isUploading) => {
        setUploadingStates(prev => ({
            ...prev,
            [index]: isUploading
        }));
    };

    // Testimonials verwalten
    const testimonials = editData.testimonials || [];
    
    const addTestimonial = () => {
        const newTestimonials = [...testimonials, {
            text: '',
            author: '',
            title: '',
            image: ''
        }];
        handleInputChange({ target: { name: 'testimonials', value: newTestimonials } });
    };

    const removeTestimonial = (index) => {
        const newTestimonials = testimonials.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'testimonials', value: newTestimonials } });
    };

    const updateTestimonial = (index, field, value) => {
        const newTestimonials = [...testimonials];
        newTestimonials[index] = { ...newTestimonials[index], [field]: value };
        handleInputChange({ target: { name: 'testimonials', value: newTestimonials } });
    };

    const handleTestimonialImageUpload = async (index, file) => {
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            setTooBigFileInfo({ size: file.size, name: file.name });
            setShowImageTooBigPopup(true);
            return;
        }

        setUploadingState(index, true);
        
        try {
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            const fieldName = `testimonial_${index}_image`;
            const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                }, 
                (error) => {
                    console.error('Upload error:', error);
                    setUploadingState(index, false);
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        updateTestimonial(index, 'image', downloadURL);
                        setUploadingState(index, false);
                        console.log('✅ Testimonial image uploaded:', downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error('Firebase import error:', error);
            alert('Upload failed. Please try again.');
            setUploadingState(index, false);
        }
    };

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 SimplisticTestimonial initialized - layout variations now active');
            
            // ✅ TEMPLATE-DEFAULTS KORRIGIEREN für Mobile Properties
            
            // Fix testimonialSpacingMobile (falls undefined)
            if (editData.testimonialSpacingMobile === undefined && editData.testimonialSpacing !== undefined) {
                console.log('🔧 Setting testimonialSpacingMobile template default');
                handleInputChange({ target: { name: 'testimonialSpacingMobile', value: Math.max(15, (editData.testimonialSpacing || 30) - 10), type: 'range' }});
            }
            
            // ✅ ENTFERNT: Altes testimonialPadding System nicht mehr verwendet
            
            // Fix testimonialPaddingHorizontalMobile (falls undefined)
            if (editData.testimonialPaddingHorizontalMobile === undefined && editData.testimonialPaddingHorizontal !== undefined) {
                console.log('🔧 Setting testimonialPaddingHorizontalMobile template default');
                handleInputChange({ target: { name: 'testimonialPaddingHorizontalMobile', value: Math.max(12, (editData.testimonialPaddingHorizontal || 24) - 6), type: 'range' }});
            }
            
            // Fix testimonialPaddingVerticalMobile (falls undefined)
            if (editData.testimonialPaddingVerticalMobile === undefined && editData.testimonialPaddingVertical !== undefined) {
                console.log('🔧 Setting testimonialPaddingVerticalMobile template default');
                handleInputChange({ target: { name: 'testimonialPaddingVerticalMobile', value: Math.max(12, (editData.testimonialPaddingVertical || 24) - 6), type: 'range' }});
            }
            
            // Fix testimonialCardHeightMobile (falls undefined)
            if (editData.testimonialCardHeightMobile === undefined && editData.testimonialCardHeight !== undefined) {
                console.log('🔧 Setting testimonialCardHeightMobile template default');
                handleInputChange({ target: { name: 'testimonialCardHeightMobile', value: Math.max(200, (editData.testimonialCardHeight || 300) - 50), type: 'range' }});
            }
            
            // Fix testimonialTextMaxWidthMobile (falls undefined)
            if (editData.testimonialTextMaxWidthMobile === undefined && editData.testimonialTextMaxWidth !== undefined) {
                console.log('🔧 Setting testimonialTextMaxWidthMobile template default');
                handleInputChange({ target: { name: 'testimonialTextMaxWidthMobile', value: Math.max(300, (editData.testimonialTextMaxWidth || 600) - 100), type: 'range' }});
            }
            
            // Fix testimonialCardMaxWidthMobile (falls undefined)
            if (editData.testimonialCardMaxWidthMobile === undefined && editData.testimonialCardMaxWidth !== undefined) {
                console.log('🔧 Setting testimonialCardMaxWidthMobile template default');
                handleInputChange({ target: { name: 'testimonialCardMaxWidthMobile', value: Math.max(320, (editData.testimonialCardMaxWidth || 600) - 50), type: 'range' }});
            }
            
            // Fix contentSpacingMobile (falls undefined)
            if (editData.contentSpacingMobile === undefined && editData.contentSpacing !== undefined) {
                console.log('🔧 Setting contentSpacingMobile template default');
                handleInputChange({ target: { name: 'contentSpacingMobile', value: Math.max(20, (editData.contentSpacing || 30) - 10), type: 'range' }});
            }
            
            // Fix fullHeightImageWidthMobile (falls undefined) - Default 70%
            if (editData.fullHeightImageWidthMobile === undefined) {
                console.log('🔧 Setting fullHeightImageWidthMobile template default to 70%');
                handleInputChange({ target: { name: 'fullHeightImageWidthMobile', value: 70, type: 'range' }});
            }
            
            // ✅ KRITISCH: Template-Defaults für Card Max Width korrigieren
            if (editData.testimonialCardMaxWidth === undefined) {
                console.log('🔧 Setting testimonialCardMaxWidth template default to 450px');
                handleInputChange({ target: { name: 'testimonialCardMaxWidth', value: 450, type: 'range' }});
            }
            
            if (editData.testimonialCardMaxWidthMobile === undefined) {
                console.log('🔧 Setting testimonialCardMaxWidthMobile template default to 350px');
                handleInputChange({ target: { name: 'testimonialCardMaxWidthMobile', value: 350, type: 'range' }});
            }
            
            // ✅ KRITISCH: GLASSMORPHISM PROPERTIES - Race Condition Fix
            if (editData.testimonialGlassmorphism === undefined) {
                console.log('🔧 Setting testimonialGlassmorphism template default to false');
                handleInputChange({ target: { name: 'testimonialGlassmorphism', value: false, type: 'checkbox' }});
            }
            
            if (editData.testimonialGlassStrength === undefined) {
                console.log('🔧 Setting testimonialGlassStrength template default to 10px');
                handleInputChange({ target: { name: 'testimonialGlassStrength', value: 10, type: 'range' }});
            }
            
            // ✅ KRITISCH: BACKGROUND IMAGE PROPERTIES - Race Condition Fix
            if (editData.showBackgroundImage === undefined) {
                console.log('🔧 Setting showBackgroundImage template default to false');
                handleInputChange({ target: { name: 'showBackgroundImage', value: false, type: 'checkbox' }});
            }
            
            if (editData.backgroundImage === undefined) {
                console.log('🔧 Setting backgroundImage template default to empty string');
                handleInputChange({ target: { name: 'backgroundImage', value: '', type: 'text' }});
            }
            
            if (editData.backgroundImageOpacity === undefined) {
                console.log('🔧 Setting backgroundImageOpacity template default to 50%');
                handleInputChange({ target: { name: 'backgroundImageOpacity', value: 50, type: 'range' }});
            }
            
            if (editData.backgroundImageSize === undefined) {
                console.log('🔧 Setting backgroundImageSize template default to cover');
                handleInputChange({ target: { name: 'backgroundImageSize', value: 'cover', type: 'select' }});
            }
            
            if (editData.backgroundImagePosition === undefined) {
                console.log('🔧 Setting backgroundImagePosition template default to center');
                handleInputChange({ target: { name: 'backgroundImagePosition', value: 'center', type: 'select' }});
            }
            
            if (editData.backgroundImageBlur === undefined) {
                console.log('🔧 Setting backgroundImageBlur template default to 0px');
                handleInputChange({ target: { name: 'backgroundImageBlur', value: 0, type: 'range' }});
            }
            
            // ✅ KRITISCH: IMAGE BORDER PROPERTIES - Race Condition Fix
            if (editData.testimonialImageBorder === undefined) {
                console.log('🔧 Setting testimonialImageBorder template default to true');
                handleInputChange({ target: { name: 'testimonialImageBorder', value: true, type: 'checkbox' }});
            }
            
            if (editData.testimonialImageBorderWidth === undefined) {
                console.log('🔧 Setting testimonialImageBorderWidth template default to 2px');
                handleInputChange({ target: { name: 'testimonialImageBorderWidth', value: 2, type: 'range' }});
            }
            
        }, 100);
        
        return () => clearTimeout(timer);
    }, [editData.testimonialSpacingMobile, editData.testimonialCardHeightMobile, 
        editData.testimonialTextMaxWidthMobile, editData.testimonialCardMaxWidthMobile, editData.contentSpacingMobile, 
        editData.testimonialPaddingHorizontalMobile, editData.testimonialPaddingVerticalMobile,
        editData.testimonialSpacing, editData.testimonialCardHeight, 
        editData.testimonialTextMaxWidth, editData.testimonialCardMaxWidth, editData.contentSpacing, 
        editData.testimonialPaddingHorizontal, editData.testimonialPaddingVertical,
        // ✅ GLASSMORPHISM & BACKGROUND IMAGE DEPENDENCIES - Race Condition Fix
        editData.testimonialGlassmorphism, editData.testimonialGlassStrength,
        editData.showBackgroundImage, editData.backgroundImage, editData.backgroundImageOpacity,
        editData.backgroundImageSize, editData.backgroundImagePosition, editData.backgroundImageBlur,
        editData.testimonialImageBorder, editData.testimonialImageBorderWidth,
        handleInputChange]);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.testimonialLayout;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 SimplisticTestimonial: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 SimplisticTestimonial: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 SimplisticTestimonial REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'classic-grid': {
                height: 'auto',
                heightMobile: 'auto',
                testimonialSpacing: 30,
                testimonialPaddingHorizontal: 24,
                testimonialPaddingVertical: 24,
                testimonialBorderRadius: 0,
                testimonialShadow: false,
                testimonialBorder: false,
                testimonialBackgroundColor: '#ffffff', // ✅ GLASSMORPHISM FIX: Explizit setzen
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                // ✅ CARD DIMENSIONS: Standard für Grid-Layout
                testimonialCardHeight: 300,
                testimonialCardHeightMobile: 250,
                testimonialCardMaxWidth: 800,
                testimonialCardMaxWidthMobile: 350,
                // ✅ KONSISTENTE BASIS-WERTE für alle Layouts
                testimonialTextMaxWidth: 600,
                testimonialTextMaxWidthMobile: 500,
                fullHeightImageWidth: 120,
                fullHeightImageHeight: 250,
                fullHeightImagePosition: 'left',
                testimonialImageMode: false, // Round images für Grid
                contentSpacing: 30,
                contentSpacingMobile: 20,
                testimonialsTopSpacing: 0,
                testimonialsTopSpacingMobile: 0,
                testimonialHorizontalGap: 30,
                testimonialHorizontalGapMobile: 20,
                testimonialPaddingHorizontalMobile: 20, // ✅ Mobile: Card Padding Horizontal 0%
                testimonialPaddingVerticalMobile: 20, 
            },
            'paper-cards': {
                height: 'auto',
                heightMobile: 1200,
                testimonialSpacing: 25,
                testimonialPaddingHorizontal: 28,
                testimonialPaddingVertical: 28,
                testimonialBorderRadius: 8,
                testimonialShadow: true,
                testimonialBorder: true,
                testimonialBackgroundColor: '#ffffff', // ✅ GLASSMORPHISM FIX: Explizit setzen
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                // ✅ CARD DIMENSIONS: Paper Cards Standard
                testimonialCardHeight: 320,
                testimonialCardHeightMobile: 260,
                testimonialCardMaxWidth: 800,
                testimonialCardMaxWidthMobile: 340,
                // ✅ KONSISTENTE BASIS-WERTE für alle Layouts
                testimonialTextMaxWidth: 600,
                testimonialTextMaxWidthMobile: 500,
                fullHeightImageWidth: 120,
                fullHeightImageHeight: 250,
                fullHeightImagePosition: 'left',
                testimonialImageMode: false, // Round images für Grid
                contentSpacing: 30,
                contentSpacingMobile: 20
            },
            'luxury-cards': {
                height: 'auto',
                heightMobile: 1200,
                testimonialSpacing: 30,
                testimonialPaddingHorizontal: 32,
                testimonialPaddingVertical: 32,
                testimonialPaddingHorizontalMobile: 0, // ✅ Mobile: Card Padding Horizontal 0%
                testimonialPaddingVerticalMobile: 0, // ✅ Mobile: Card Padding Vertical 0%
                testimonialBorderRadius: 12,
                testimonialShadow: true,
                testimonialBorder: true,
                testimonialBackgroundColor: '#faf8f5', // ✅ Helle, warme Luxury-Farbe
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                // ✅ MOBILE IMAGE: 100% Breite für Luxury Cards
                fullHeightImageWidthMobile: 100,
                // ✅ CARD DIMENSIONS: Luxury Cards Standard
                testimonialCardHeight: 340,
                testimonialCardHeightMobile: 280,
                testimonialCardMaxWidth: 500,
                testimonialCardMaxWidthMobile: 350,
                // ✅ KONSISTENTE BASIS-WERTE für alle Layouts
                testimonialTextMaxWidth: 600,
                testimonialTextMaxWidthMobile: 500,
                fullHeightImageWidth: 120,
                fullHeightImageHeight: 250,
                fullHeightImagePosition: 'left',
                testimonialImageMode: false, // Überschrieben durch Layout-spezifische Settings
                contentSpacing: 30,
                contentSpacingMobile: 20
            },
            'full-width-classic': {
                height: 'auto',
                heightMobile: 'auto',
                testimonialSpacing: 20,
                testimonialPaddingHorizontal: 30,
                testimonialPaddingVertical: 30,
                testimonialBorderRadius: 0,
                testimonialShadow: false,
                testimonialBorder: true,
                testimonialBackgroundColor: '#ffffff', // ✅ GLASSMORPHISM FIX: Explizit setzen
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                // ✅ IMAGE MODE: Default Round für Classic
                testimonialImageMode: false, // false = round
                fullHeightImageWidth: 120,
                fullHeightImageHeight: 180,
                fullHeightImagePosition: 'left',
                // ✅ CARD DIMENSIONS: Full-Width Classic
                testimonialCardHeight: 180,
                testimonialCardHeightMobile: 200,
                testimonialCardMaxWidth: 800, // ✅ Full-Width braucht mehr Platz
                testimonialCardMaxWidthMobile: 350,
                // ✅ KONSISTENTE BASIS-WERTE für alle Layouts
                testimonialTextMaxWidth: 600,
                testimonialTextMaxWidthMobile: 500,
                contentSpacing: 30,
                contentSpacingMobile: 20
            },
            'full-width-bordered': {
                height: 'auto',
                heightMobile: 'auto',
                testimonialSpacing: 20,
                testimonialPaddingHorizontal: 30,
                testimonialPaddingVertical: 30,
                testimonialBorderRadius: 14,
                testimonialShadow: true,
                testimonialBorder: true,
                testimonialBackgroundColor: '#ffffff', // ✅ GLASSMORPHISM FIX: Explizit setzen
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                // ✅ IMAGE MODE: Full-Height für Bordered (eleganter Look)
                testimonialImageMode: true, // true = full-height
                fullHeightImageWidth: 140,
                fullHeightImageHeight: 200,
                fullHeightImagePosition: 'left',
                // ✅ CARD DIMENSIONS: Full-Width Bordered
                testimonialCardHeight: 200,
                testimonialCardHeightMobile: 220,
                testimonialCardMaxWidth: 800, // ✅ Full-Width braucht mehr Platz
                testimonialCardMaxWidthMobile: 350,
                // ✅ KONSISTENTE BASIS-WERTE für alle Layouts
                testimonialTextMaxWidth: 600,
                testimonialTextMaxWidthMobile: 500,
                contentSpacing: 30,
                contentSpacingMobile: 20
            },
            'full-width-fancy': {
                height: 'auto',
                heightMobile: 'auto',
                testimonialSpacing: 20,
                testimonialPaddingHorizontal: 0, // ✅ Card Padding Horizontal 0px
                testimonialPaddingVertical: 0, // ✅ Card Padding Vertical 12px
                testimonialBorderRadius: 0, // ✅ Card Border Radius auf 0
                borderRadius: 0, // ✅ Widget Border Radius auf 0
                testimonialShadow: false, // ✅ Show Card Shadow auf off
                testimonialBorder: false, // ✅ Show Card Border auf off
                testimonialBackgroundColor: 'transparent', // ✅ GLASSMORPHISM FIX: Fancy = transparent
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                // ✅ IMAGE MODE: Full-Height für Fancy Look
                testimonialImageMode: true, // true = full-height
                fullHeightImageWidth: 200, // ✅ Full Height Image Width 200px
                fullHeightImageHeight: 300, // ✅ Full Height Image Height 300px
                fullHeightImagePosition: 'left',
                // ✅ CARD DIMENSIONS: Full-Width Fancy
                testimonialCardHeight: 300,
                testimonialCardHeightMobile: 250,
                testimonialCardMaxWidth: 1000, // ✅ Fancy: Sehr breite Cards
                testimonialCardMaxWidthMobile: 350,
                // ✅ KONSISTENTE BASIS-WERTE für alle Layouts
                testimonialTextMaxWidth: 600,
                testimonialTextMaxWidthMobile: 500,
                contentSpacing: 30,
                contentSpacingMobile: 20
            },
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            // ✅ FIX: Preserve user settings that aren't in layout variations
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { 
                ...editData, 
                ...variations,
                // ✅ PRESERVE: Glassmorphism user settings
                testimonialGlassmorphism: editData.testimonialGlassmorphism || false,
                testimonialGlassStrength: editData.testimonialGlassStrength || 10,
                // ✅ PRESERVE: Image Border user settings  
                testimonialImageBorder: editData.testimonialImageBorder !== undefined ? editData.testimonialImageBorder : true,
                testimonialImageBorderWidth: editData.testimonialImageBorderWidth || 2,
                // ✅ PRESERVE: Background Image user settings (mit sicheren Fallbacks)
                showBackgroundImage: editData.showBackgroundImage || false,
                backgroundImage: editData.backgroundImage || '',
                backgroundImageOpacity: editData.backgroundImageOpacity || 50,
                backgroundImageSize: editData.backgroundImageSize || 'cover',
                backgroundImagePosition: editData.backgroundImagePosition || 'center',
                backgroundImageBlur: editData.backgroundImageBlur || 0
            };
            
            console.log(`🔧 Batch updating editData:`, {
                before: { height: editData.height, heightMobile: editData.heightMobile },
                after: { height: updatedEditData.height, heightMobile: updatedEditData.heightMobile }
            });
            
            // ✅ DIREKTE onUpdate statt einzelne handleInputChange calls
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ SimplisticTestimonial layout variations applied via batch update`);
        }
    }, [editData.testimonialLayout, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Titel und Untertitel mit Show/Hide Toggles
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Main Title (H2)',
            type: 'input',
            placeholder: 'What Our Customers Say',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        // ✅ NEU: Highlight Words Feld hinzufügen
        {
            fieldName: 'titleHighlightWords',
            label: 'Title - Highlight Words',
            type: 'input',
            placeholder: 'Customers, Amazing (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'titleHighlightFont',
            fontType: 'small', // ✅ Anderer Font-Pool als Main Title
            effectField: 'titleHighlightEffect'
        },
        {
            fieldName: 'subtitleText',
            label: 'Subtitle Text (P)',
            type: 'textarea',
            placeholder: 'Discover what our amazing customers have to say about their experience...',
            rows: 2,
            fontField: 'subtitleFont',
            fontType: 'small',
            effectField: 'subtitleEffect'
        }
    ];


    // Color Picker Configuration
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#000000',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'subtitleColor',
                label: 'Subtitle',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'testimonialBackgroundColor',
                label: 'Card Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'testimonialTextColor',
                label: 'Card Text',
                defaultValue: '#333333',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'testimonialBorderColor',
                label: 'Card Border',
                defaultValue: '#e0e0e0',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'testimonialAuthorColor',
                label: 'Author Name',
                defaultValue: '#000000',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'testimonialTitleColor',
                label: 'Author Title',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'testimonialImageBorderColor',
                label: 'Image Border',
                defaultValue: '#e0e0e0',
                icon: '/img/background.svg'
            }
        ]
    ];

    // Font Settings Configuration
    const fontConfig = [
        {
            label: 'H2 - Title',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '32',
            defaultWeight: '600'
        },
        // ✅ NEU: Highlight Font Settings hinzufügen
        {
            label: 'Title - Highlight Words',
            sizeField: 'titleHighlightSize',
            weightField: 'titleHighlightWeight',
            defaultSize: '32', // Gleich wie Title
            defaultWeight: '800'
        },
        {
            label: 'P - Subtitle',
            sizeField: 'subtitleSize',
            weightField: 'subtitleWeight',
            defaultSize: '18',
            defaultWeight: '400'
        },
        {
            label: 'Testimonial Text',
            sizeField: 'testimonialTextSize',
            weightField: 'testimonialTextWeight',
            defaultSize: '16',
            defaultWeight: '400'
        },
        {
            label: 'Author Name',
            sizeField: 'testimonialAuthorSize',
            weightField: 'testimonialAuthorWeight',
            defaultSize: '16',
            defaultWeight: '600'
        },
        {
            label: 'Author Title',
            sizeField: 'testimonialTitleSize',
            weightField: 'testimonialTitleWeight',
            defaultSize: '14',
            defaultWeight: '400'
        }
    ];

    // 🚀 REVOLUTIONARY LAYOUT SETTINGS - Custom Properties für Simplistic Testimonial Widget
    const customLayoutProperties = [
        {
            section: 'Content Settings',
            icon: 'fa-align-left',
            properties: [
                {
                    id: 'maxWidth',
                    label: 'Widget Max Width',
                    min: 600,
                    max: 2000,
                    step: 50,
                    unit: 'px',
                    icon: '📄',
                    description: 'Maximum width of the entire widget content',
                    defaultValue: 1000
                },
                {
                    id: 'subtitleMaxWidth',
                    label: 'Subtitle Max Width',
                    min: 400,
                    max: 1000,
                    step: 50,
                    unit: 'px',
                    icon: '📝',
                    description: 'Maximum width of the subtitle text',
                    defaultValue: 700
                },
                {
                    id: 'contentSpacing',
                    label: 'Content Spacing',
                    min: 20,
                    max: 80,
                    step: 10,
                    unit: 'px',
                    icon: '📄',
                    description: 'Space between title, subtitle and testimonials',
                    defaultValue: 30,
                    responsive: true
                },
                {
                    id: 'testimonialsTopSpacing',
                    label: 'Testimonials Top Spacing',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Additional space above testimonials grid',
                    defaultValue: 0,
                    responsive: true
                }
            ]
        },
        {
            section: 'Testimonial Settings',
            icon: 'fa-quote-left',
            properties: [
                {
                    id: 'testimonialImageSize',
                    label: 'Round Image Size',
                    min: 60,
                    max: 120,
                    step: 10,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Size of round profile images (round mode only)',
                    defaultValue: 80
                },
                {
                    id: 'fullHeightImageWidth',
                    label: 'Full Height Image Width',
                    min: 80,
                    max: 400,
                    step: 10,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Width of full-height side images (desktop only)',
                    defaultValue: 120,
                    desktopOnly: true
                },
                {
                    id: 'fullHeightImageHeight',
                    label: 'Full Height Image Height',
                    min: 150,
                    max: 800,
                    step: 10,
                    unit: 'px',
                    icon: '📏',
                    description: 'Height of full-height side images (desktop only)',
                    defaultValue: 250,
                    desktopOnly: true
                },
                {
                    id: 'fullHeightImageWidth',
                    label: 'Mobile Image Width',
                    min: 50,
                    max: 100,
                    step: 5,
                    unit: '%',
                    icon: '📱',
                    description: 'Width of image on mobile (column layout)',
                    defaultValue: 70,
                    mobileOnly: true,
                    forceMobileField: true
                },
                {
                    id: 'testimonialSpacing',
                    label: 'Card Spacing',
                    min: 0,
                    max: 150,
                    step: 5,
                    unit: 'px',
                    icon: '📋',
                    description: 'Space between testimonial cards',
                    defaultValue: 30,
                    responsive: true
                },
                {
                    id: 'testimonialHorizontalGap',
                    label: 'Horizontal Gap',
                    min: 5,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    icon: '↔️',
                    description: 'Horizontal gap between grid containers',
                    defaultValue: 30,
                    responsive: true
                },
                {
                    id: 'testimonialPaddingHorizontal',
                    label: 'Card Padding Horizontal',
                    min: 0,
                    max: 80,
                    step: 2,
                    unit: 'px',
                    icon: '↔️',
                    description: 'Horizontal padding (left & right) of testimonial cards',
                    defaultValue: 24,
                    responsive: true
                },
                {
                    id: 'testimonialPaddingVertical',
                    label: 'Card Padding Vertical',
                    min: 0,
                    max: 60,
                    step: 2,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Vertical padding (top & bottom) of testimonial cards',
                    defaultValue: 24,
                    responsive: true
                },
                {
                    id: 'testimonialCardHeight',
                    label: 'Card Height',
                    min: 200,
                    max: 500,
                    step: 10,
                    unit: 'px',
                    icon: '📏',
                    description: 'Height of testimonial cards',
                    defaultValue: 300,
                    responsive: true
                },
                {
                    id: 'testimonialTextMaxWidth',
                    label: 'Text Max Width',
                    min: 200,
                    max: 800,
                    step: 20,
                    unit: 'px',
                    icon: '📝',
                    description: 'Maximum width of testimonial text content',
                    defaultValue: 600,
                    responsive: true
                },
                {
                    id: 'testimonialCardMaxWidth',
                    label: 'Card Max Width',
                    min: 300,
                    max: 1200,
                    step: 20,
                    unit: 'px',
                    icon: '📐',
                    description: 'Maximum width of entire testimonial cards',
                    defaultValue: 600,
                    responsive: true
                }
            ]
        }
    ];

    // ✅ ADVANCED SETTINGS CONFIGURATION - Widget-spezifische Properties + Visibility Toggles
    const advancedConfig = [
        // Visibility Controls
        {
            fieldName: 'showTitleText',
            label: 'Show Title',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showSubtitleText',
            label: 'Show Subtitle',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        
        // Text Alignment
        {
            fieldName: 'textAlignment',
            label: 'Text Alignment',
            type: 'select',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/down.svg'
        },
        {
            fieldName: 'textAlignmentMobile',
            label: 'Mobile Text Alignment',
            type: 'select',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/down.svg'
        },

        // Testimonial Card Behavior
        {
            fieldName: 'testimonialBorder',
            label: 'Show Card Border',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'testimonialShadow',
            label: 'Show Card Shadow',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'testimonialItalic',
            label: 'Italic Text',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'testimonialQuotes',
            label: 'Show Quotes',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },

        // Image Mode System (nur für Full-Width Layouts)
        {
            fieldName: 'testimonialImageMode',
            label: 'Full Height Side Image',
            type: 'toggle',
            defaultValue: false, // false = round, true = full-height
            icon: '/img/background.svg'
        },
        {
            fieldName: 'fullHeightImagePosition',
            label: 'Full Height Image Position',
            type: 'select',
            options: [
                { value: 'left', label: 'Left Side' },
                { value: 'right', label: 'Right Side' }
            ],
            defaultValue: 'left',
            icon: '/img/down.svg'
        },

        // Border Radius Sliders
        {
            fieldName: 'borderRadius',
            label: 'Widget Border Radius',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'testimonialBorderRadius',
            label: 'Card Border Radius',
            type: 'slider',
            defaultValue: 12,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'testimonialGlassmorphism',
            label: 'Glassmorphism Effect',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/glass.svg',
            description: 'Enable glassmorphism effect with backdrop blur'
        },
        // ✅ Glassmorphism Stärke - nur sichtbar wenn Toggle aktiv
        ...(editData.testimonialGlassmorphism ? [
            {
                fieldName: 'testimonialGlassStrength',
                label: 'Glass Effect Strength',
                type: 'slider',
                defaultValue: 10,
                min: 5,
                max: 25,
                step: 1,
                unit: 'px',
                icon: '/img/blur.svg',
                description: 'Backdrop blur intensity for glassmorphism'
            }
        ] : []),
        {
            fieldName: 'testimonialImageBorder',
            label: 'Image Border',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/border.svg',
            description: 'Show border around testimonial images'
        },
        // ✅ Image Border Width - nur sichtbar wenn Border Toggle aktiv
        ...(editData.testimonialImageBorder !== false ? [
            {
                fieldName: 'testimonialImageBorderWidth',
                label: 'Image Border Width',
                type: 'slider',
                defaultValue: 2,
                min: 1,
                max: 8,
                step: 1,
                unit: 'px',
                icon: '/img/border.svg',
                description: 'Width of the testimonial image border'
            }
        ] : []),
        
        // ✅ NEU: Highlight Settings hinzufügen
        {
            fieldName: 'titleHighlightColor',
            label: 'Title Highlight Color',
            type: 'color',
            defaultValue: '#D4A574',
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'titleHighlightWeight',
            label: 'Title Highlight Weight',
            type: 'dropdown',
            options: [
                { value: '300', label: 'Light' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi Bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra Bold' },
                { value: '900', label: 'Black' }
            ],
            defaultValue: '800',
            icon: '/img/text-fat.svg'
        },
        // ✅ NEU: Margin Controls für präzise Positionierung
        {
            fieldName: 'titleHighlightMarginLeft',
            label: 'Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,        // ✅ Erweitert: -50px für Überlappungen
            max: 50,         // ✅ Erweitert: +50px für große Abstände
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'titleHighlightMarginRight',
            label: 'Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        }
    ];

    // Layout Settings Configuration (Empty - moved to Advanced Settings)
    const layoutConfig = [];

    return (
        <div>
            <div className="top-widget-edit-container">

                {/* ✅ LAYOUT SELECTOR 2025 */}
                <LayoutSelector2025
                    widgetType="simplicisticTestimonial"
                    selectedLayout={editData.testimonialLayout || 'classic-grid'}
                    onLayoutChange={handleLayoutChange}
                />

                {/* 🚀 BACKGROUND IMAGE CONTAINER - direkt nach Layouts */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    showToggle={true}
                    toggleLabel="Show Background Image"
                    toggleFieldName="showBackgroundImage"
                    toggleChecked={editData.showBackgroundImage || false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Background Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 50,
                            min: 10,
                            max: 100,
                            step: 5,
                            unit: '%',
                            icon: '/img/opacity.svg',
                            description: 'Adjust background image opacity'
                        },
                        {
                            type: 'select',
                            label: 'Background Size',
                            fieldName: 'backgroundImageSize',
                            defaultValue: 'cover',
                            options: [
                                { value: 'cover', label: 'Cover (Fill)' },
                                { value: 'contain', label: 'Contain (Fit)' },
                                { value: 'auto', label: 'Original Size' }
                            ],
                            icon: '/img/resize.svg',
                            description: 'How the background image should fit'
                        },
                        {
                            type: 'select',
                            label: 'Background Position',
                            fieldName: 'backgroundImagePosition',
                            defaultValue: 'center',
                            options: [
                                { value: 'center', label: 'Center' },
                                { value: 'top', label: 'Top' },
                                { value: 'bottom', label: 'Bottom' },
                                { value: 'left', label: 'Left' },
                                { value: 'right', label: 'Right' },
                                { value: 'top left', label: 'Top Left' },
                                { value: 'top right', label: 'Top Right' },
                                { value: 'bottom left', label: 'Bottom Left' },
                                { value: 'bottom right', label: 'Bottom Right' }
                            ],
                            icon: '/img/position.svg',
                            description: 'Position of the background image'
                        },
                        {
                            type: 'slider',
                            label: 'Blur Intensity',
                            fieldName: 'backgroundImageBlur',
                            defaultValue: 0,
                            min: 0,
                            max: 10,
                            step: 0.5,
                            unit: 'px',
                            icon: '/img/blur.svg',
                            description: 'Blur applied to background image (0 = no blur, 10 = maximum blur)'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 2. TEXT SETTINGS - Revolutionäres Text-System */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />

                {/* 3. TESTIMONIALS MANAGEMENT */}
                <TestimonialsManagement 
                    testimonials={testimonials}
                    addTestimonial={addTestimonial}
                    removeTestimonial={removeTestimonial}
                    updateTestimonial={updateTestimonial}
                    handleTestimonialImageUpload={handleTestimonialImageUpload}
                    uploadingStates={uploadingStates}
                    editData={editData}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 4. ADVANCED SETTINGS - Nur noch Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* 5. MORE SETTINGS - Colors & Fonts only */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={layoutConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>

            {/* Image Too Big Popup */}
            <ImageTooBigPopup 
                isOpen={showImageTooBigPopup}
                onClose={() => setShowImageTooBigPopup(false)}
                fileSize={tooBigFileInfo.size}
                fileName={tooBigFileInfo.name}
                maxSize={2}
            />
        </div>
    );
}

export default SimplisticTestimonialEdit;
